import { useCallback, useEffect, useMemo, useState } from 'react';
import { getWeather, WeatherData } from '../services/weatherService';

const WEATHER_STALE_TIME = 30 * 60 * 1000;
const WEATHER_STORAGE_PREFIX = 'weather-cache:v2:';
const weatherCache = new Map<string, { loadedAt: number; data: WeatherData }>();

const readStoredWeather = (cacheKey: string) => {
  try {
    const raw = window.localStorage.getItem(`${WEATHER_STORAGE_PREFIX}${cacheKey}`);
    if (!raw) return null;
    return JSON.parse(raw) as { loadedAt: number; data: WeatherData };
  } catch {
    return null;
  }
};

const writeStoredWeather = (cacheKey: string, data: WeatherData) => {
  try {
    window.localStorage.setItem(`${WEATHER_STORAGE_PREFIX}${cacheKey}`, JSON.stringify({ loadedAt: Date.now(), data }));
  } catch {
    // local storage can be unavailable in private windows
  }
};

export const useWeather = (latitude?: number | null, longitude?: number | null) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = useMemo(() => {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
    return `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
  }, [latitude, longitude]);

  const loadWeather = useCallback(
    async (force = false) => {
      if (!cacheKey || typeof latitude !== 'number' || typeof longitude !== 'number') {
        setWeather(null);
        setError(null);
        return;
      }

      const cached = weatherCache.get(cacheKey);
      if (!force && cached && Date.now() - cached.loadedAt < WEATHER_STALE_TIME) {
        setWeather(cached.data);
        return;
      }

      const stored = readStoredWeather(cacheKey);
      if (!force && stored && Date.now() - stored.loadedAt < WEATHER_STALE_TIME) {
        weatherCache.set(cacheKey, stored);
        setWeather(stored.data);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getWeather(latitude, longitude);
        weatherCache.set(cacheKey, { loadedAt: Date.now(), data });
        writeStoredWeather(cacheKey, data);
        setWeather(data);
      } catch {
        const fallback = weatherCache.get(cacheKey) ?? readStoredWeather(cacheKey);
        if (fallback?.data) {
          setWeather(fallback.data);
        }
        setError('Unable to load weather.');
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, latitude, longitude],
  );

  useEffect(() => {
    void loadWeather();
  }, [loadWeather]);

  useEffect(() => {
    if (!cacheKey) return undefined;
    const intervalId = window.setInterval(() => {
      void loadWeather(true);
    }, WEATHER_STALE_TIME);

    return () => window.clearInterval(intervalId);
  }, [cacheKey, loadWeather]);

  return {
    current: weather?.current ?? null,
    weeklyForecast: weather?.weeklyForecast ?? [],
    loading,
    error,
    reload: () => loadWeather(true),
  };
};
